/**
 * Utility functions for working with language URL parameters
 * Now uses dynamic language detection instead of hardcoded languages
 */

export type SupportedLanguage = string; // Made more flexible since languages come from backend

/**
 * Generates a URL with a language parameter
 * @param language - The language code to add to the URL
 * @param baseUrl - The base URL (defaults to current location)
 * @param supportedLanguages - Array of supported language codes (optional validation)
 * @returns URL with the language parameter added
 * 
 * @example
 * generateLanguageUrl('fr') // Current URL with ?lang=fr
 * generateLanguageUrl('de', '/dashboard') // '/dashboard?lang=de'
 * generateLanguageUrl('es', '/jobs?filter=remote') // '/jobs?filter=remote&lang=es'
 */
export const generateLanguageUrl = (
    language: string,
    baseUrl?: string,
    supportedLanguages?: string[]
): string => {
    // Validate against supported languages if provided
    if (supportedLanguages && !supportedLanguages.includes(language)) {
        throw new Error(`Unsupported language: ${language}. Supported languages: ${supportedLanguages.join(', ')}`);
    }

    const url = new URL(baseUrl || window.location.href, window.location.origin);
    url.searchParams.set('lang', language);

    // If baseUrl was provided and is relative, return just the pathname + search
    if (baseUrl && !baseUrl.startsWith('http')) {
        return url.pathname + url.search;
    }

    return url.toString();
};

/**
 * Gets the current language from the URL parameters
 * @param supportedLanguages - Array of supported language codes for validation (optional)
 * @returns The language code from URL or null if not present/supported
 */
export const getLanguageFromUrl = (supportedLanguages?: string[]): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang')?.toLowerCase();

    if (!langParam) return null;

    // Validate against supported languages if provided
    if (supportedLanguages && !supportedLanguages.includes(langParam)) {
        return null;
    }

    return langParam;
};

/**
 * Removes the language parameter from a URL
 * @param url - The URL to clean (defaults to current location)
 * @returns URL without the language parameter
 */
export const removeLanguageFromUrl = (url?: string): string => {
    const targetUrl = new URL(url || window.location.href, window.location.origin);
    targetUrl.searchParams.delete('lang');

    // If original url was relative, return relative result
    if (url && !url.startsWith('http')) {
        return targetUrl.pathname + targetUrl.search;
    }

    return targetUrl.toString();
};

/**
 * Checks if a language code is supported
 * @param language - The language code to check
 * @param supportedLanguages - Array of supported language codes
 * @returns true if the language is supported
 */
export const isSupportedLanguage = (language: string, supportedLanguages: string[]): boolean => {
    return supportedLanguages.includes(language.toLowerCase());
}; 